import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { Box, Flex, Grid, Spinner } from "theme-ui";

import { getClientOverview, getClientActivity } from "api/Clients";
import { getAssignableContacts, AssignableContact } from "api/LocationClientProfiles";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import BasicButton from "components/forms/BasicButton";
import { themevals } from "theme/themevals";
import ClientPricingOverrides from "./ClientPricingOverrides";

/**
 * MASTER_PLAN.md §3.9 #13 remainder — Client Detail Overview tab.
 * Design: docs/maintenance-module/design/OVERVIEW_TAB.md.
 * Mockup: docs/maintenance-module/mockups/14-client-detail-overview.svg.
 *
 * Layout: masthead health cards, then a two-column body — Primary
 * Contacts / Billing snapshot / HQ address on the left, cross-record
 * Activity feed on the right. Renders identically for plan and
 * non-plan clients (activePlans just reads 0 / null for the latter).
 *
 * "View full activity log →" from the mockup is intentionally omitted —
 * no destination page exists yet (see design doc §4, deferred to
 * Phase 3's audit-log arc).
 */
export default function OverviewTab({ clientId, client }: { clientId: string; client: any }) {
  const navigate = useNavigate();

  const [overview, setOverview] = useState<any>(null);
  const [loadingOverview, setLoadingOverview] = useState(true);

  const [contacts, setContacts] = useState<AssignableContact[] | null>(null);

  const [activity, setActivity] = useState<any[]>([]);
  const [activityHasMore, setActivityHasMore] = useState(false);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingOverview(true);
      const res = await getClientOverview(clientId);
      if (!cancelled) {
        setOverview(res);
        setLoadingOverview(false);
      }
    })();
    (async () => {
      const rows = await getAssignableContacts(clientId);
      if (!cancelled) setContacts(Array.isArray(rows) ? rows.slice(0, 2) : []);
    })();
    (async () => {
      setLoadingActivity(true);
      const res = await getClientActivity(clientId, { limit: 25 });
      if (!cancelled) {
        setActivity(Array.isArray(res?.items) ? res.items : []);
        setActivityHasMore(!!res?.hasMore);
        setLoadingActivity(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const loadMoreActivity = async () => {
    if (activity.length === 0) return;
    setLoadingMore(true);
    const oldest = activity[activity.length - 1];
    const res = await getClientActivity(clientId, { limit: 25, before: oldest?.at });
    setActivity((prev) => [...prev, ...(Array.isArray(res?.items) ? res.items : [])]);
    setActivityHasMore(!!res?.hasMore);
    setLoadingMore(false);
  };

  const health = overview?.healthCards;
  const billing = overview?.billing?.rollingNinetyDay;

  return (
    <Box>
      {/* Masthead stat cards */}
      <Grid gap={"16px"} columns={[2, 4]} sx={{ marginBottom: "28px" }}>
        <StatCard
          label="Open RFQs"
          loading={loadingOverview}
          value={health?.openRfqs?.count ?? 0}
          detail={
            health
              ? `${health.openRfqs.planTagged} plan-tagged · ${health.openRfqs.oneOff} one-off`
              : undefined
          }
        />
        <StatCard
          label="Unpaid invoices"
          loading={loadingOverview}
          value={formatCurrency(health?.unpaidInvoices?.totalDollars ?? 0)}
          detail={
            health
              ? `${health.unpaidInvoices.count} open${
                  health.unpaidInvoices.mostOverdueDays
                    ? ` · 1 past due ${health.unpaidInvoices.mostOverdueDays}d`
                    : ""
                }`
              : undefined
          }
        />
        <StatCard
          label="Active plans"
          loading={loadingOverview}
          value={health?.activePlans?.count ?? 0}
          detail={
            health?.activePlans?.primary
              ? `${health.activePlans.primary.name}${
                  health.activePlans.primary.committedSiteCount
                    ? ` · ${health.activePlans.primary.committedSiteCount} committed`
                    : ""
                }`
              : health
                ? "no active plan"
                : undefined
          }
        />
        <StatCard
          label="Last activity"
          loading={loadingOverview}
          value={health?.lastActivity ? moment(health.lastActivity.at).format("h:mm A") : "—"}
          detail={health?.lastActivity?.subject}
        />
      </Grid>

      <Grid gap={"28px"} columns={[1, "1fr 1fr"]}>
        {/* Left column */}
        <Box>
          <SectionLabel>Primary contacts</SectionLabel>
          <Card>
            {!contacts ? (
              <Box sx={{ padding: "24px", textAlign: "center" }}>
                <Spinner size={18} />
              </Box>
            ) : contacts.length === 0 ? (
              <Box sx={{ padding: "20px" }}>
                <P color={themevals.colors.dkgrey} fontSize={"13px"} marginTop={0} marginBottom={0}>
                  No contacts yet.
                </P>
              </Box>
            ) : (
              contacts.map((c, idx) => (
                <Flex
                  key={c.id}
                  sx={{
                    alignItems: "center",
                    gap: "14px",
                    padding: "16px 20px",
                    borderBottom: idx === contacts.length - 1 ? "none" : "1px solid",
                    borderColor: "inputOutline",
                  }}
                >
                  <Flex
                    sx={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      background: "#F1EFE8",
                      color: themevals.colors.dkgrey,
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "Poppins",
                      fontSize: "11px",
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(c.name)}
                  </Flex>
                  <Box sx={{ minWidth: 0 }}>
                    <Span fontFamily={"Poppins"} fontWeight={600} fontSize={"13px"} color={themevals.colors.black}>
                      {c.name || "Unnamed"}
                    </Span>
                    <Box>
                      <Span fontFamily={"Poppins"} fontSize={"11px"} color={themevals.colors.dkgrey}>
                        {[c.contactEmail, c.phone].filter(Boolean).join(" · ")}
                      </Span>
                    </Box>
                  </Box>
                </Flex>
              ))
            )}
          </Card>

          <Box sx={{ marginTop: "28px" }}>
            <SectionLabel>Billing snapshot</SectionLabel>
            <Card>
              <Box sx={{ padding: "20px" }}>
                <BillingRow
                  label="Payment terms"
                  value={client?.terms ? `Net ${client.terms}` : "Not set (defaults to Net 30)"}
                  sublineNode={
                    /*
                     * SESSION 45 GAP-45 — mockup 14 sub-line beneath
                     * Payment terms: `Plan override: urgent WOs may
                     * pay upfront`. The rule is real (urgent Corrigo
                     * WOs on EMCOR can pay ahead of the corporate
                     * net-60 cadence — see NotesTab pinned example
                     * "Net-60 for capital account WOs. Urgent WOs may
                     * pay upfront if pre-approved."). Rendered when a
                     * client has any pricing override configured on a
                     * plan; for a v1 we render whenever the payload
                     * exposes `paymentOverride`. `hasActivePlan` isn't
                     * on the client payload yet — wiring that flag
                     * from ServicePlanService.listOpenForClient into
                     * the Client find() relations is a follow-on.
                     */
                    (client as any)?.paymentOverride ? (
                      <Span
                        fontFamily={"Poppins"}
                        fontSize={"11px"}
                        color={themevals.colors.dkgrey}
                      >
                        Plan override: urgent WOs may pay upfront
                      </Span>
                    ) : null
                  }
                />
                <BillingRow
                  label="Zoho customer"
                  value={client?.name}
                  pill={client?.external?.zoho?.lastSyncSuccessful ? "Auto-sync on" : undefined}
                />
                {loadingOverview ? (
                  <Spinner size={16} />
                ) : (
                  <BillingRow
                    label="Rolling 90-day"
                    value={`${formatCurrency(billing?.invoicedDollars ?? 0)} invoiced · ${formatCurrency(
                      billing?.openDollars ?? 0,
                    )} open`}
                    isLast
                  />
                )}
              </Box>
            </Card>
          </Box>

          <Box sx={{ marginTop: "28px" }}>
            <SectionLabel>HQ address</SectionLabel>
            <Card>
              <Box sx={{ padding: "20px" }}>
                <P fontSize={"13px"} margin={0} color={themevals.colors.black}>
                  {[client?.address?.line1, client?.address?.line2].filter(Boolean).join(", ")}
                </P>
                <P fontSize={"13px"} marginTop={"2px"} marginBottom={0} color={themevals.colors.black}>
                  {[client?.address?.city, client?.address?.state, client?.address?.postalCode]
                    .filter(Boolean)
                    .join(" ")}
                </P>
                {/* SESSION 45 GAP-46 — mockup 14 tags the HQ block
                 * `Corporate HQ · not a serviceable site`. The tag
                 * keeps a crew from being dispatched to the corporate
                 * office and reads at a glance which address is the
                 * billing point versus where work happens. Renders
                 * whenever the client has a plan (only maintenance
                 * clients have the distinction — one-off clients don't
                 * separate HQ from service sites). Read from an
                 * `isCorporateHq` flag on the client if present; if not
                 * available yet, the surface is quietly not rendered. */}
                {(client as any)?.isCorporateHq && (
                  <Subtext
                    fontSize={"11px"}
                    fontFamily={"Poppins"}
                    margin={0}
                    marginTop={"8px"}
                    color={themevals.colors.dkgrey}
                  >
                    Corporate HQ · not a serviceable site
                  </Subtext>
                )}
              </Box>
            </Card>
          </Box>
        </Box>

        {/* Right column — activity feed */}
        <Box>
          <SectionLabel subtitle="all record types">Recent activity</SectionLabel>
          {/* F3c: cap the feed height and scroll INTERNALLY so a long
              activity list no longer stretches the whole page. "Load more"
              still appends older rows (25 at a time via the before cursor);
              they land inside this scroll area rather than growing the page. */}
          <Card sx={{ maxHeight: "560px", overflowY: "auto" }}>
            {loadingActivity ? (
              <Box sx={{ padding: "40px", textAlign: "center" }}>
                <Spinner />
              </Box>
            ) : activity.length === 0 ? (
              <Box sx={{ padding: "24px" }}>
                <P color={themevals.colors.dkgrey} fontSize={"13px"} marginTop={0} marginBottom={0}>
                  No activity yet.
                </P>
              </Box>
            ) : (
              <>
                {activity.map((item, idx) => (
                  <ActivityRow
                    key={`${item.kind}-${item.at}-${idx}`}
                    item={item}
                    isLast={idx === activity.length - 1 && !activityHasMore}
                    onClick={item.linkTo ? () => navigate(item.linkTo) : undefined}
                  />
                ))}
                {activityHasMore && (
                  <Box sx={{ padding: "14px 20px", textAlign: "center" }}>
                    <BasicButton
                      type="button"
                      scheme="secondaryOutline"
                      onClick={loadMoreActivity}
                      $submitting={loadingMore}
                    >
                      Load more
                    </BasicButton>
                  </Box>
                )}
              </>
            )}
          </Card>
        </Box>
      </Grid>
      {/* Pricing overrides for this client: fuel surcharge waiver + IE minimum. Staff only (hides itself otherwise). */}
      <ClientPricingOverrides clientId={clientId} />
    </Box>
  );
}

function StatCard({
  label,
  value,
  detail,
  loading,
}: {
  label: string;
  value: React.ReactNode;
  detail?: string;
  loading: boolean;
}) {
  return (
    <Box sx={{ background: "#F1EFE8", borderRadius: "8px", padding: "14px 16px" }}>
      <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>
        {label}
      </Subtext>
      <Box sx={{ marginTop: "4px" }}>
        {loading ? (
          <Spinner size={18} />
        ) : (
          <Span fontFamily={"Poppins"} fontWeight={500} fontSize={"22px"} color={themevals.colors.black}>
            {value}
          </Span>
        )}
      </Box>
      {!loading && detail && (
        <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>
          {detail}
        </Subtext>
      )}
    </Box>
  );
}

function SectionLabel({ children, subtitle }: { children: React.ReactNode; subtitle?: string }) {
  return (
    <Flex sx={{ alignItems: "baseline", gap: "10px", marginBottom: "12px" }}>
      <H5 fontFamily="Poppins" fontWeight={600} margin={0} padding={0} fontSize={"14px"} color={themevals.colors.black}>
        {children}
      </H5>
      {subtitle && (
        <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>
          {subtitle}
        </Subtext>
      )}
    </Flex>
  );
}

function Card({ children, sx }: { children: React.ReactNode; sx?: any }) {
  return (
    <Box
      sx={{
        background: "white",
        border: "1px solid",
        borderColor: "inputOutline",
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0px 1px 2px 0px rgba(113, 125, 150, 0.03)",
        ...(sx ?? {}),
      }}
    >
      {children}
    </Box>
  );
}

function BillingRow({
  label,
  value,
  pill,
  isLast,
  sublineNode,
}: {
  label: string;
  value?: string;
  pill?: string;
  isLast?: boolean;
  /** Optional secondary line rendered below the primary value.
   * SESSION 45 GAP-45 — used for the `Plan override: urgent WOs may
   * pay upfront` annotation on Payment terms. */
  sublineNode?: React.ReactNode;
}) {
  return (
    <Box sx={{ marginBottom: isLast ? 0 : "16px" }}>
      <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>
        {label}
      </Subtext>
      <Flex sx={{ alignItems: "center", gap: "10px", marginTop: "2px" }}>
        <Span fontFamily={"Poppins"} fontSize={"13px"} color={themevals.colors.black}>
          {value || "—"}
        </Span>
        {pill && (
          <Box
            sx={{
              display: "inline-block",
              background: "#EAF3DE",
              color: "#27500A",
              fontSize: "11px",
              fontFamily: "Poppins",
              padding: "2px 10px",
              borderRadius: "9px",
            }}
          >
            {pill}
          </Box>
        )}
      </Flex>
      {sublineNode && (
        <Box sx={{ marginTop: "3px" }}>{sublineNode}</Box>
      )}
    </Box>
  );
}

const ACTIVITY_DOT_COLOR: Record<string, string> = {
  wo_received: "#27500A",
  wo_converted: "#185FA5",
  project_completed: "#27500A",
  invoice_paid: "#5f5e5a",
  note_added: "#5f5e5a",
  sites_added: "#633806",
};

function ActivityRow({
  item,
  isLast,
  onClick,
}: {
  item: any;
  isLast: boolean;
  onClick?: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        padding: "14px 20px",
        borderBottom: isLast ? "none" : "1px solid",
        borderColor: "inputOutline",
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { background: "#FAFAF8" } : undefined,
      }}
    >
      <Flex sx={{ alignItems: "center", gap: "8px", marginBottom: "4px" }}>
        <Box
          sx={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: ACTIVITY_DOT_COLOR[item.kind] || "#5f5e5a",
            flexShrink: 0,
          }}
        />
        <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>
          {formatRelativeTime(item.at)}
        </Subtext>
      </Flex>
      <P fontSize={"13px"} margin={0} color={themevals.colors.black}>
        {item.subject}
      </P>
      {item.subtext && (
        <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>
          {item.subtext}
        </Subtext>
      )}
    </Box>
  );
}

function formatRelativeTime(at: string): string {
  const m = moment(at);
  if (m.isSame(moment(), "day")) return `Today · ${m.format("h:mm A")}`;
  if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  const days = moment().diff(m, "days");
  if (days < 7) return `${days} days ago`;
  return m.format("MMM D, YYYY");
}

function formatCurrency(value: number): string {
  return Number(value ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function getInitials(name: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
